import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { IFilterObj } from 'src/components/filters';
import {
  getDefaultOptionsForFormFields,
  transformDynamicFormValueToFilterPayload
} from 'src/components/filters/utils/filter-form-utils';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { IDynamicFormValue } from 'src/lib';
import useFilterFetch from '../../../hooks/use-filter-fetch.hook';
import { Filters2 } from '../../../components';
import { IAccessConfig } from '../../../types/access-configuration.model';
import { ConfigDiffFinder } from '../../../utils';
import {
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../store/filter-slice';
import { fetchFilterFields } from '../store/filter-slice-async-thunk';

interface IFiltersWrapperProps {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
  externalReset: boolean;
  accessConfig: IAccessConfig;
  setIsFilterValueChanged: (value: boolean) => void;
  // eslint-disable-next-line react/require-default-props
  onValueChange?: (value: IDynamicFormValue) => void;
  onValueApply: (
    filters: IFilterObj[],
    isReset: boolean,
    formValue: IDynamicFormValue
  ) => void;
  onFilterFieldsFetched: (
    filters: IFilterObj[],
    value: IDynamicFormValue
  ) => void;
}

export default function FiltersWrapper({
  sourceId,
  moduleId,
  scenarioId,
  externalReset,
  accessConfig,
  onFilterFieldsFetched,
  onValueApply,
  onValueChange,
  setIsFilterValueChanged
}: IFiltersWrapperProps) {
  const appDispatch = useAppDispatch();
  const {
    globalFilterCombinations,
    globalFilterFormFields,
    globalFilterValue,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);
  const { getFilterFields, isFetchingFilterFields } = useFilterFetch({
    filterFieldFetchThunk: () =>
      appDispatch(fetchFilterFields({ sourceId, scenarioId, moduleId })),
    sourceId,
    moduleId,
    scenarioId
  });

  useEffect(() => {
    if (moduleId !== null) {
      getFilterFields().then((response) => {
        const filters = transformDynamicFormValueToFilterPayload({
          combinations: response.combinations,
          formFields: response.fields,
          formValue: response.formValue
        });
        onFilterFieldsFetched(filters, response.formValue);
      });
    }
  }, [moduleId]);

  return (
    <Grid item xs={12} mb={1}>
      {isFetchingFilterFields ? (
        FilterLoadingSkeleton(4)
      ) : (
        <Filters2
          formValueInState={globalFilterValue}
          formFields={globalFilterFormFields}
          externalReset={externalReset}
          initialValues={globalFilterInitialValues}
          onChange={(value) => {
            appDispatch(
              setGlobalFilterFieldOptions(value.fieldNameToOptionsMap)
            );
            setIsFilterValueChanged(
              ConfigDiffFinder.isObjectDifferent(
                value.newFormValues,
                globalFilterValue
              )
            );
            if (typeof onValueChange === 'function') {
              onValueChange(value.newFormValues);
            }
          }}
          isApplyDisabled={!accessConfig?.edit_filters}
          isResetDisabled={!accessConfig?.edit_filters}
          combinations={globalFilterCombinations}
          onReset={(value) => {
            setIsFilterValueChanged(false);
            appDispatch(setGlobalFilterValues(value));
            appDispatch(
              setGlobalFilterFieldOptions(
                getDefaultOptionsForFormFields(
                  globalFilterFormFields,
                  globalFilterCombinations,
                  globalFilterInitialValues
                )
              )
            );
            const filters = transformDynamicFormValueToFilterPayload({
              combinations: globalFilterCombinations,
              formFields: globalFilterFormFields,
              formValue: value
            });
            onValueApply(filters, true, value);
          }}
          onApplyFilters={(value) => {
            setIsFilterValueChanged(false);
            appDispatch(setGlobalFilterValues(value));
            const filters = transformDynamicFormValueToFilterPayload({
              combinations: globalFilterCombinations,
              formFields: globalFilterFormFields,
              formValue: value
            });
            onValueApply(filters, false, value);
          }}
        />
      )}
    </Grid>
  );
}
